// Apparences des personnages (joueurs et PNJ) : des animations découpées dans une planche de sprites,
// définies dans l'éditeur d'apparences. Partagé par le serveur (validation), le jeu (rendu) et l'administration.
import type { Direction } from './protocol.ts';

export const CHARACTER_ANIMATIONS = ['idle', 'walk', 'run'] as const;
export type CharacterAnimation = (typeof CHARACTER_ANIMATIONS)[number];

export const CHARACTER_DIRECTIONS = ['down', 'up', 'left', 'right'] as const satisfies readonly Direction[];

export const ANIMATION_LABELS: Readonly<Record<CharacterAnimation, string>> = { idle: 'Immobile', walk: 'Marche', run: 'Course' };
export const DIRECTION_LABELS: Readonly<Record<Direction, string>> = { down: '↓ Bas', up: '↑ Haut', left: '← Gauche', right: '→ Droite' };

/** Une image de l'animation : un rectangle de la planche, en pixels. */
export interface CharacterFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CharacterClip {
  frames: CharacterFrame[];
  /** Durée d'une image, en millisecondes. */
  ms: number;
  /** Dessiné en miroir (une planche de profil ne dessine souvent qu'un côté). */
  mirror: boolean;
}

export type CharacterClips = Partial<Record<CharacterAnimation, Partial<Record<Direction, CharacterClip>>>>;

/**
 * « sheet » : une planche complète (personnage déjà habillé). « custom » : la planche est un corps nu, habillé de
 * pièces (vêtements, visage, cheveux…) dessinées par-dessus avec les mêmes découpes.
 */
export const CHARACTER_KINDS = ['sheet', 'custom'] as const;
export type CharacterKind = (typeof CHARACTER_KINDS)[number];
export const KIND_LABELS: Readonly<Record<CharacterKind, string>> = { sheet: 'Planche complète', custom: 'Personnalisable (corps + pièces)' };

/** Emplacements des pièces, dans l'ordre où elles se superposent (du dessous vers le dessus). */
export const PART_SLOTS = ['shoes', 'legs', 'torso', 'face', 'hair', 'hat'] as const;
export type PartSlot = (typeof PART_SLOTS)[number];
export const SLOT_LABELS: Readonly<Record<PartSlot, string>> = {
  shoes: 'Chaussures', legs: 'Bas', torso: 'Haut', face: 'Visage', hair: 'Cheveux', hat: 'Couvre-chef',
};

/**
 * Une pièce d'une apparence personnalisable : une planche disposée exactement comme celle du corps (mêmes
 * images aux mêmes coordonnées, dans toutes les directions), dessinée par-dessus.
 */
export interface CharacterPart {
  id: string;
  name: string;
  slot: PartSlot;
  image: string;
  /** Dessinée en blanc et gris : prend la couleur choisie dans la tenue (multipliée). */
  tintable: boolean;
}

/** Pièce portée à un emplacement, et sa couleur (pièces teintables, sinon null). */
export interface OutfitPiece {
  part: string;
  color: string | null;
}

export type CharacterOutfit = Partial<Record<PartSlot, OutfitPiece>>;

export const newCharacterPartId = (): string => `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export interface CharacterDesignInput {
  name: string;
  kind: CharacterKind;
  /** Planche de sprites (fichier PNG de /assets/characters) : pour une apparence personnalisable, le corps nu. */
  image: string;
  /** Pixels de la planche qui correspondent à la largeur d'une case du jeu (règle la taille affichée). */
  pixelsPerTile: number;
  /** Pixels vides sous les pieds, en bas de chaque image : ils débordent sous la case. */
  footOffset: number;
  /** Couleur de fond rendue transparente (planches sans canal alpha), ou null. */
  transparentColor: string | null;
  /** Écart toléré autour de la couleur transparente (0 à 255, par canal). */
  tolerance: number;
  /** Proposée aux nouveaux joueurs, au choix de leur apparence de départ. */
  playable: boolean;
  /** Proposée devant une garde-robe (indépendamment de `playable`). */
  wardrobe: boolean;
  animations: CharacterClips;
  /** Pièces disponibles (apparence personnalisable ; vide sinon). */
  parts: CharacterPart[];
  /** Tenue portée : une pièce au plus par emplacement. */
  outfit: CharacterOutfit;
}

export interface CharacterDesign extends CharacterDesignInput {
  id: number;
  /**
   * Preset : l'apparence personnalisable dont il reprend tout (planche, découpes, pièces), avec sa propre tenue,
   * fixe (`outfit`) ; null pour une apparence ordinaire.
   */
  presetOf: number | null;
}

/** Ce que l'administration règle d'un preset : son nom, l'apparence personnalisable de départ et la tenue portée. */
export interface CharacterPresetInput {
  name: string;
  baseId: number;
  outfit: CharacterOutfit;
  playable: boolean;
  wardrobe: boolean;
}

/** Sa tenue se choisit-elle (PNJ, joueur) ? Pas pour une planche complète, ni pour un preset (tenue fixe). */
export const isCustomizable = (design: Pick<CharacterDesign, 'kind' | 'presetOf'>): boolean => design.kind === 'custom' && design.presetOf === null;

/** Un preset porté avec sa tenue : l'apparence de départ, sous son nom, avec la tenue du preset. */
export function expandPreset(id: number, preset: CharacterPresetInput, base: CharacterDesign): CharacterDesign {
  return { ...base, id, name: preset.name, playable: preset.playable, wardrobe: preset.wardrobe, outfit: cleanOutfit(preset.outfit, base.parts), presetOf: base.id };
}

/** Planches de personnages téléversées dans l'administration (onglet Apparences). */
export const CHARACTERS_URL = '/uploads/characters/';

export const CHARACTER_LIMITS = { maxParts: 256, maxFrames: 32, minMs: 20, maxMs: 5000, maxPixelsPerTile: 1920 } as const;

/** Durée d'un pas en marchant et en courant, en ms (le serveur accepte la cadence de course). */
export const WALK_STEP_MS = 160;
export const RUN_STEP_MS = 100;

const HORIZONTAL_OPPOSITE: Partial<Record<Direction, Direction>> = { left: 'right', right: 'left' };

/** Animations de repli quand une animation n'est pas définie : la course retombe sur la marche, etc. */
const ANIMATION_FALLBACKS: Readonly<Record<CharacterAnimation, readonly CharacterAnimation[]>> = {
  idle: ['idle', 'walk', 'run'],
  walk: ['walk', 'run', 'idle'],
  run: ['run', 'walk', 'idle'],
};

/** Ce qui sera réellement joué pour une animation et une direction, et d'où cela vient. */
export interface ResolvedClip {
  clip: CharacterClip;
  /** Miroir final (celui du clip, inversé s'il est emprunté au côté opposé). */
  mirror: boolean;
  animation: CharacterAnimation;
  direction: Direction;
  /** Vrai si le clip vient d'une autre animation ou direction. */
  borrowed: boolean;
}

/**
 * Clip joué pour (animation, direction) : celui défini, sinon le côté opposé en miroir (gauche ↔ droite),
 * sinon la vue de face, sinon n'importe quelle direction ; puis les mêmes règles sur l'animation de repli.
 */
export function resolveClip(animations: CharacterClips, animation: CharacterAnimation, direction: Direction): ResolvedClip | null {
  for (const candidate of ANIMATION_FALLBACKS[animation]) {
    const clips = animations[candidate];
    if (!clips) continue;
    const usable = (dir: Direction | undefined) => (dir && clips[dir]?.frames.length ? clips[dir] : undefined);
    const exact = usable(direction);
    const borrowed = candidate !== animation;
    if (exact) return { clip: exact, mirror: exact.mirror, animation: candidate, direction, borrowed };
    const opposite = HORIZONTAL_OPPOSITE[direction];
    const mirrored = usable(opposite);
    if (mirrored && opposite) return { clip: mirrored, mirror: !mirrored.mirror, animation: candidate, direction: opposite, borrowed: true };
    for (const dir of CHARACTER_DIRECTIONS) {
      const clip = usable(dir);
      if (clip) return { clip, mirror: clip.mirror, animation: candidate, direction: dir, borrowed: true };
    }
  }
  return null;
}

export const hasAnyFrame = (animations: CharacterClips): boolean =>
  CHARACTER_ANIMATIONS.some((animation) => CHARACTER_DIRECTIONS.some((dir) => (animations[animation]?.[dir]?.frames.length ?? 0) > 0));

/**
 * Pièces portées, dans l'ordre de dessin (aucune pour une planche complète) : celles de `outfit` (la tenue d'un PNJ
 * ou d'un joueur), sinon la tenue par défaut de l'apparence. Les pièces qui n'existent plus sont ignorées.
 */
export function wornParts(
  design: Pick<CharacterDesignInput, 'kind' | 'parts' | 'outfit'>, outfit: CharacterOutfit | null = null,
): { part: CharacterPart; color: string | null }[] {
  if (design.kind !== 'custom') return [];
  const pieces = outfit ?? design.outfit;
  return PART_SLOTS.flatMap((slot) => {
    const piece = pieces[slot];
    const part = piece && design.parts.find((candidate) => candidate.id === piece.part && candidate.slot === slot);
    return part ? [{ part, color: part.tintable ? piece.color : null }] : [];
  });
}

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * Tenue reçue (formulaire, message d'un joueur) ramenée aux pièces de l'apparence : une pièce existante et du bon
 * emplacement par emplacement, une couleur #rrggbb pour les pièces teintables. Le reste est ignoré.
 */
export function cleanOutfit(raw: unknown, parts: readonly CharacterPart[]): CharacterOutfit {
  const outfit: CharacterOutfit = {};
  if (typeof raw !== 'object' || raw === null) return outfit;
  for (const slot of PART_SLOTS) {
    const piece = (raw as Record<string, unknown>)[slot];
    if (typeof piece !== 'object' || piece === null) continue;
    const { part: id, color } = piece as Record<string, unknown>;
    const part = parts.find((candidate) => candidate.id === id && candidate.slot === slot);
    if (!part) continue;
    outfit[slot] = { part: part.id, color: part.tintable && typeof color === 'string' && HEX_COLOR.test(color) ? color.toLowerCase() : null };
  }
  return outfit;
}
